/**
 * xdr.js — XDR integration domain service.
 * Integration CRUD, alert listing, sync, explain.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
} from './_core';

export async function createXDRIntegration(payload, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/xdr/integrations/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload || {}),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Create XDR integration failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'XDR integration create');
}

export async function listXDRIntegrations(token = null) {
  const response = await fetchWithAuthRetry(toApiUrl('/xdr/integrations/'), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `List XDR integrations failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'XDR integrations list');
}

export async function testXDRIntegrationConnection(integrationId, token = null) {
  const safeId = Number(integrationId);
  if (!Number.isFinite(safeId)) throw new Error('Valid integration id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/xdr/integrations/${safeId}/test/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `XDR connection test failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'XDR integration test');
}

export async function syncXDRIntegrationAlerts(integrationId, token = null) {
  const safeId = Number(integrationId);
  if (!Number.isFinite(safeId)) throw new Error('Valid integration id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/xdr/integrations/${safeId}/sync/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `XDR sync failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'XDR integration sync');
}

export async function listXDRAlerts(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/xdr/alerts/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `XDR alerts fetch failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'XDR alerts list');
}

export async function getXDRAlertDetail(alertId, token = null) {
  const safeId = Number(alertId);
  if (!Number.isFinite(safeId)) throw new Error('Valid alert id is required.');

  const response = await fetchWithAuthRetry(toApiUrl(`/xdr/alerts/${safeId}/`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `XDR alert detail failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'XDR alert detail');
}

export async function explainXDRAlert(alertId, token = null) {
  const safeId = Number(alertId);
  if (!Number.isFinite(safeId)) throw new Error('Valid alert id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/xdr/alerts/${safeId}/explain/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `XDR alert explain failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'XDR alert explain');
}
